<template>
  <div class="tag-operation-row">
    <!-- Label -->
    <div class="tag-operation-row__label">
      <strong>{{ definition.name }}</strong>
      <span class="text-muted small ml-1">({{ inputTypeLabel }})</span>
    </div>

    <!-- Controls -->
    <div class="tag-operation-row__controls">
      <!-- Tag Semplici: text, number, date, select singola -->
      <template v-if="!isMultiple">
        <!-- Text Input -->
        <input
          v-if="definition.input_type === 'text'"
          type="text"
          v-model="setValue"
          :placeholder="definition.input_config.placeholder || ''"
          :maxlength="definition.input_config.max_length"
          class="form-control form-control-sm tag-operation-row__input"
        />

        <!-- Number Input -->
        <input
          v-else-if="definition.input_type === 'number'"
          type="number"
          v-model="setValue"
          :min="definition.input_config.min_value"
          :max="definition.input_config.max_value"
          :step="definition.input_config.step || 1"
          class="form-control form-control-sm tag-operation-row__input"
        />

        <!-- Date Input -->
        <input
          v-else-if="definition.input_type === 'date'"
          type="date"
          v-model="setValue"
          :min="definition.input_config.min_date"
          :max="definition.input_config.max_date"
          class="form-control form-control-sm tag-operation-row__input"
        />

        <!-- Select Singola -->
        <select
          v-else-if="definition.input_type === 'select' && !definition.input_config.multiple"
          v-model="setValue"
          class="form-control form-control-sm tag-operation-row__input"
        >
          <option value="">-</option>
          <option
            v-for="option in definition.input_config.options"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </option>
        </select>

        <!-- Buttons -->
        <CButton
          size="sm"
          color="primary"
          class="ml-2"
          :disabled="!setValue || applying"
          @click="onApplySet"
        >
          <CSpinner v-if="applying === 'set'" size="sm"/>
          <span v-else v-translate translate-context="tag_manager.action">Imposta</span>
        </CButton>

        <CButton
          size="sm"
          color="outline-danger"
          class="ml-1"
          :disabled="applying"
          @click="onApplyClear"
        >
          <CSpinner v-if="applying === 'clear'" size="sm"/>
          <span v-else v-translate translate-context="tag_manager.action">Svuota</span>
        </CButton>
      </template>

      <!-- Checkbox / Select Multiple -->
      <template v-else-if="definition.input_type === 'checkbox' || (definition.input_type === 'select' && definition.input_config.multiple)">
        <div class="tag-operation-row__checkboxes">
          <div
            v-for="option in definition.input_config.options"
            :key="option.value"
            class="custom-control custom-checkbox custom-control-inline"
          >
            <input
              type="checkbox"
              :id="`${definition.code}_${option.value}`"
              :checked="isInSelectedValues(option.value)"
              @change="toggleSelectedValue(option.value, $event.target.checked)"
              class="custom-control-input"
            />
            <label
              :for="`${definition.code}_${option.value}`"
              class="custom-control-label"
            >
              {{ option.label }}
            </label>
          </div>
        </div>

        <CButton
          size="sm"
          color="success"
          class="ml-2"
          :disabled="selectedValues.length === 0 || applying"
          @click="onApplyAdd"
        >
          <CSpinner v-if="applying === 'add'" size="sm"/>
          <span v-else v-translate translate-context="tag_manager.action">Aggiungi</span>
        </CButton>

        <CButton
          size="sm"
          color="danger"
          class="ml-1"
          :disabled="selectedValues.length === 0 || applying"
          @click="onApplyRemove"
        >
          <CSpinner v-if="applying === 'remove'" size="sm"/>
          <span v-else v-translate translate-context="tag_manager.action">Rimuovi</span>
        </CButton>
      </template>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TagOperationItem',
  props: {
    definition: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      applying: false, // 'set', 'clear', 'add', 'remove' or false
      setValue: '',
      selectedValues: [] // Per checkbox/select multiple
    }
  },
  computed: {
    isMultiple() {
      const {input_type, input_config} = this.definition
      return input_type === 'checkbox' ||
        input_type === 'tags' ||
        (input_type === 'select' && input_config && input_config.multiple)
    },
    inputTypeLabel() {
      const labels = {
        text: 'testo',
        number: 'numero',
        date: 'data',
        select: this.definition.input_config?.multiple ? 'selezione multipla' : 'selezione',
        checkbox: 'checkbox',
        tags: 'tag'
      }
      return labels[this.definition.input_type] || this.definition.input_type
    }
  },
  methods: {
    // Checkbox / Select Multiple
    isInSelectedValues(value) {
      return this.selectedValues.includes(value)
    },
    toggleSelectedValue(value, checked) {
      if (checked) {
        if (!this.selectedValues.includes(value)) {
          this.selectedValues.push(value)
        }
      } else {
        this.selectedValues = this.selectedValues.filter(v => v !== value)
      }
    },

    // Apply actions
    async onApplySet() {
      if (!this.setValue) return
      this.applying = 'set'
      try {
        await this.emitPromised('apply', {
          code: this.definition.code,
          operation: {action: 'set', value: this.setValue}
        })
      } finally {
        this.applying = false
      }
    },

    async onApplyClear() {
      this.applying = 'clear'
      try {
        await this.emitPromised('apply', {
          code: this.definition.code,
          operation: {action: 'clear'}
        })
      } finally {
        this.applying = false
      }
    },

    async onApplyAdd() {
      if (this.selectedValues.length === 0) return
      this.applying = 'add'
      try {
        await this.emitPromised('apply', {
          code: this.definition.code,
          operation: {action: 'add', values: [...this.selectedValues]}
        })
        this.selectedValues = []
      } finally {
        this.applying = false
      }
    },

    async onApplyRemove() {
      if (this.selectedValues.length === 0) return
      this.applying = 'remove'
      try {
        await this.emitPromised('apply', {
          code: this.definition.code,
          operation: {action: 'remove', values: [...this.selectedValues]}
        })
        this.selectedValues = []
      } finally {
        this.applying = false
      }
    },

    reset() {
      this.setValue = ''
      this.selectedValues = []
      this.applying = false
    }
  }
}
</script>

<style scoped>
.tag-operation-row {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-bottom: 1px solid #e9ecef;
}

.tag-operation-row:last-child {
  border-bottom: none;
}

.tag-operation-row__label {
  min-width: 180px;
  flex-shrink: 0;
}

.tag-operation-row__controls {
  display: flex;
  align-items: center;
  flex-grow: 1;
}

.tag-operation-row__input {
  max-width: 200px;
}

.tag-operation-row__checkboxes {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
}

.tag-operation-row__checkboxes .custom-control {
  margin-bottom: 0;
}
</style>
